/**
 * Staff chat API client (SR-14): the Notifications routes under `/api/Notifications/staff-chat/...`, on the same
 * gateway as the WhatsApp inbox (`/api/Notifications/whatsapp/inbox`).
 *
 * Every call goes through the shared `fetchWithAuth` (bearer token, OrganizationId and the usual header set).
 * Errors are `{ "error": "<code>" }`; a 404 answers a thread of another org or one the caller is not in.
 *
 * PRIVACY: `StaffChatApiError.message` carries the status and the error code only, never a request body.
 */
import { fetchWithAuth } from '@/shared/api/api-client';
import {
  mapMessage,
  mapPage,
  mapPerson,
  mapThread,
  type Raw,
  type StaffChatMessage,
  type StaffChatPage,
  type StaffChatPerson,
  type StaffChatThread,
} from '../staff-chat-types';

export const STAFF_CHAT_BASE = '/api/Notifications/staff-chat';

/** Contract limits. */
export const STAFF_CHAT_THREADS_TAKE = 100;
export const STAFF_CHAT_MESSAGES_TAKE = 50;
export const STAFF_CHAT_BODY_MAX = 4000;

export class StaffChatApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, code: string | null) {
    super(`Staff chat API ${status}${code ? ` ${code}` : ''}`);
    this.name = 'StaffChatApiError';
    this.status = status;
    this.code = code;
  }
}

function toError(status: number, text: string): StaffChatApiError {
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    return new StaffChatApiError(status, typeof parsed?.error === 'string' ? parsed.error : null);
  } catch {
    return new StaffChatApiError(status, null);
  }
}

async function send(endpoint: string, init: RequestInit): Promise<Response> {
  const res = await fetchWithAuth(endpoint, init);
  if (!res.ok) throw toError(res.status, await res.text());
  return res;
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

const asRaw = (v: unknown): Raw => (typeof v === 'object' && v !== null ? (v as Raw) : {});

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

const seg = encodeURIComponent;

/** `GET directory`: the org's active staff and practitioners with a login, the caller left out, sorted by name. */
export async function fetchStaffDirectory(signal?: AbortSignal): Promise<StaffChatPerson[]> {
  const res = await send(`${STAFF_CHAT_BASE}/directory`, { method: 'GET', signal });
  return mapPage(await readJson(res), mapPerson).items;
}

/** `GET threads`: newest activity first. */
export async function fetchThreads(
  req: { take?: number; cursor?: string | null; signal?: AbortSignal } = {},
): Promise<StaffChatPage<StaffChatThread>> {
  const res = await send(`${STAFF_CHAT_BASE}/threads${query({ take: req.take, cursor: req.cursor })}`, {
    method: 'GET',
    signal: req.signal,
  });
  return mapPage(await readJson(res), mapThread);
}

/** `GET threads/{id}`. 404 `thread_not_found`. */
export async function fetchThread(threadId: string, signal?: AbortSignal): Promise<StaffChatThread> {
  const res = await send(`${STAFF_CHAT_BASE}/threads/${seg(threadId)}`, { method: 'GET', signal });
  return mapThread(asRaw(await readJson(res)));
}

/** `POST threads`: find or create (one direct thread per pair; one patient thread per pair and patient). */
export async function createThread(req: { participantUserId: string; patientId?: string | null }): Promise<StaffChatThread> {
  const body: Record<string, string> = { participantUserId: req.participantUserId };
  if (req.patientId) body.patientId = req.patientId;
  const res = await send(`${STAFF_CHAT_BASE}/threads`, { method: 'POST', body: JSON.stringify(body) });
  return mapThread(asRaw(await readJson(res)));
}

/** `GET threads/{id}/messages`: newest first; `nextCursor` pages to older ones. */
export async function fetchMessages(
  threadId: string,
  req: { take?: number; cursor?: string | null; signal?: AbortSignal } = {},
): Promise<StaffChatPage<StaffChatMessage>> {
  const res = await send(
    `${STAFF_CHAT_BASE}/threads/${seg(threadId)}/messages${query({ take: req.take, cursor: req.cursor })}`,
    { method: 'GET', signal: req.signal },
  );
  return mapPage(await readJson(res), mapMessage);
}

/** `POST threads/{id}/messages` → 201 (or 200 for a repeated `clientMessageId`, which returns the first message). */
export async function postMessage(
  threadId: string,
  req: { body: string; clientMessageId: string },
): Promise<StaffChatMessage> {
  const res = await send(`${STAFF_CHAT_BASE}/threads/${seg(threadId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body: req.body, clientMessageId: req.clientMessageId }),
  });
  return mapMessage(asRaw(await readJson(res)));
}

/** `POST threads/{id}/read` → 204. Marks every message up to now read for the caller. */
export async function markThreadRead(threadId: string): Promise<void> {
  await send(`${STAFF_CHAT_BASE}/threads/${seg(threadId)}/read`, { method: 'POST' });
}

/** `GET unread` → `{ total }`. */
export async function fetchUnreadTotal(signal?: AbortSignal): Promise<number> {
  const res = await send(`${STAFF_CHAT_BASE}/unread`, { method: 'GET', signal });
  const total = asRaw(await readJson(res)).total;
  return typeof total === 'number' && Number.isFinite(total) ? Math.max(0, total) : 0;
}
